import React from 'react';
import { LucideIcon, X } from 'lucide-react';
import styles from './Chip.module.scss';

export type ChipVariant = 'filled' | 'outlined';
export type ChipColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
export type ChipSize = 'xs' | 'sm' | 'md';
export type ChipCorner = 'rounded' | 'pill' | 'square';
export type ChipWeight = 'normal' | 'semibold';

type BaseChipProps = {
    children: React.ReactNode;
    variant?: ChipVariant;
    color?: ChipColor;
    size?: ChipSize;
    corner?: ChipCorner;
    weight?: ChipWeight;
    icon?: LucideIcon;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
    clickable?: boolean;
    className?: string;
};

type ChipProps<T extends React.ElementType = 'div'> = BaseChipProps & {
    as?: T;
} & Omit<React.ComponentPropsWithoutRef<T>, keyof BaseChipProps>;

export const Chip = <T extends React.ElementType = 'div'>({
    children,
    as,
    variant = 'filled',
    color = 'primary',
    size = 'md',
    corner = 'rounded',
    weight = 'normal',
    icon: Icon,
    onClick,
    disabled = false,
    clickable = false,
    className = '',
    ...props
}: ChipProps<T>) => {
    const Component = as || 'div';

    const chipClasses = [
        styles.chip,
        styles[variant],
        styles[color],
        styles[size],
        styles[corner],
        styles[weight],
        clickable && styles.clickable,
        disabled && styles.disabled,
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const getIconSize = () => {
        switch (size) {
            case 'xs':
                return 12;
            case 'sm':
                return 14;
            case 'md':
                return 16;
            default:
                return 16;
        }
    };

    return (
        <Component className={chipClasses} disabled={disabled} {...props}>
            {Icon && <Icon size={getIconSize()} className={styles.icon} />}

            <span className={styles.label}>{children}</span>

            {onClick && (
                <button
                    type='button'
                    className={styles.removeButton}
                    onClick={onClick}
                    disabled={disabled}
                    aria-label='Remove'
                >
                    <X size={getIconSize()} />
                </button>
            )}
        </Component>
    );
};
